import { timestampDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import { CredentialType } from './espb/credential_type_pb';
import {
  ProfileAuthenticatorLock as PbProfileAuthenticatorLock,
  ProfileProfileLock as PbProfileProfileLock,
} from './espb/locks_pb';
import { RoleType } from './espb/role_type_pb';
import { SessionType } from './espb/session_type_pb';
import { SyncService } from './espb/sync_service_pb';
import {
  SyncUpdate as PbSyncUpdate,
  SyncUpdateField,
  SyncUpdate_Vault,
  SyncUpdate_Organization,
  SyncUpdate_PreliminaryProfile,
  SyncUpdateProfile as PbSyncUpdateProfile,
  SyncUpdate_RelatedProfile,
  SyncUpdate_SessionUnlock,
  SyncUpdate_User,
  SyncUpdate_Organization_AdminInfo,
} from './espb/sync_update_pb';
import { VaultType } from './espb/vault_type_pb';
import { knownEnumOrDefault, requireFieldNotEmpty } from './grpcUtils';
import {
  SyncUpdate,
  SyncUpdateSessionUnlock,
  SyncUpdateUser,
  SyncUpdateOrganization,
  SyncUpdateOrganizationAdminInfo,
  SyncUpdateProfile,
  SyncUpdateProfileAuthenticatorLock,
  SyncUpdateRelatedProfile,
  ProfileType,
  SyncUpdateWebauthnCredential,
  SyncUpdatePreliminaryProfile,
  SyncUpdateVault,
  PrimaryLoginDevice,
  SyncUpdateProfileProfileLock,
  OrganizationType,
  SyncUpdateOrganizationProfile,
} from './types';

function toSyncSessionUnlockDto(
  su?: SyncUpdate_SessionUnlock,
): SyncUpdateSessionUnlock | undefined {
  if (!su || !su.authenticatorId) {
    return undefined;
  }
  return {
    authenticatorId: su.authenticatorId,
    encryptedSecret: su.encryptedSecret,
  };
}

function toSyncUpdateProfileAuthenticatorLock(
  l: PbProfileAuthenticatorLock,
): SyncUpdateProfileAuthenticatorLock {
  return {
    authenticatorId: requireFieldNotEmpty(l.authenticatorId),
    encryptedHighSecurityProfileSeed: requireFieldNotEmpty(l.encryptedHighSecurityProfileSeed),
    encryptedStorableProfileSeed: requireFieldNotEmpty(l.encryptedStorableProfileSeed),
  };
}

function toSyncUpdateProfileProfileLock(l: PbProfileProfileLock): SyncUpdateProfileProfileLock {
  return {
    lockingProfileId: requireFieldNotEmpty(l.lockingProfileId),
    lockingProfileKeyGenerationId: requireFieldNotEmpty(l.lockingProfileKeyGenerationId),
    encryptedHighSecurityProfileSeed: requireFieldNotEmpty(l.encryptedHighSecurityProfileSeed),
    encryptedStorableProfileSeed: requireFieldNotEmpty(l.encryptedStorableProfileSeed),
  };
}

export function toSyncUpdateProfile(sa: PbSyncUpdateProfile): SyncUpdateProfile {
  return {
    id: requireFieldNotEmpty(sa.id),
    profileType: knownEnumOrDefault(sa.profileType, ProfileType),
    keyGenerationId: requireFieldNotEmpty(sa.keyGenerationId),

    email: sa.email || undefined,
    unverifiedEmail: sa.unverifiedEmail || undefined,

    organizationId: sa.organizationId || undefined,

    highSecurityIdentitySigPubKey: sa.highSecurityIdentitySigPubKey,
    highSecurityVaultKeyEncPubKey: sa.highSecurityVaultKeyEncPubKey,
    highSecurityVaultKeyEncPubKeySignature: sa.highSecurityVaultKeyEncPubKeySignature,
    highSecurityProfileSeedEncPubKey: sa.highSecurityProfileSeedEncPubKey,
    highSecurityProfileSeedEncPubKeySignature: sa.highSecurityProfileSeedEncPubKeySignature,
    storableSigPubKey: sa.storableSigPubKey,
    storableSigPubKeySignature: sa.storableSigPubKeySignature,
    storableVaultKeyEncPubKey: sa.storableVaultKeyEncPubKey,
    storableVaultKeyEncPubKeySignature: sa.storableVaultKeyEncPubKeySignature,
    storableProfileSeedEncPubKey: sa.storableProfileSeedEncPubKey,
    storableProfileSeedEncPubKeySignature: sa.storableProfileSeedEncPubKeySignature,

    authenticatorLocks: sa.authenticatorLocks.map(toSyncUpdateProfileAuthenticatorLock),
    profileLock: sa.profileLock ? toSyncUpdateProfileProfileLock(sa.profileLock) : undefined,
  };
}

function toSyncUpdatePreliminaryProfile(
  sa: SyncUpdate_PreliminaryProfile,
): SyncUpdatePreliminaryProfile {
  return {
    id: requireFieldNotEmpty(sa.id),
    email: sa.email || undefined,
    organizationId: sa.organizationId || undefined,
  };
}

function toSyncUpdateOrganizationAdminInfo(
  pbInfo: SyncUpdate_Organization_AdminInfo | undefined,
): SyncUpdateOrganizationAdminInfo | undefined {
  if (pbInfo === undefined) {
    return undefined;
  }

  const p = requireFieldNotEmpty(pbInfo.adminProfile);
  const adminProfile = {
    id: requireFieldNotEmpty(p.id),
    profileType: requireFieldNotEmpty(p.profileType),
    keyGenerationId: requireFieldNotEmpty(p.keyGenerationId),
    organizationId: requireFieldNotEmpty(p.organizationId),
    highSecurityIdentitySigPubKey: p.highSecurityIdentitySigPubKey,
    highSecurityVaultKeyEncPubKey: p.highSecurityVaultKeyEncPubKey,
    highSecurityVaultKeyEncPubKeySignature: p.highSecurityVaultKeyEncPubKeySignature,
    highSecurityProfileSeedEncPubKey: p.highSecurityProfileSeedEncPubKey,
    highSecurityProfileSeedEncPubKeySignature: p.highSecurityProfileSeedEncPubKeySignature,
    storableSigPubKey: p.storableSigPubKey,
    storableSigPubKeySignature: p.storableSigPubKeySignature,
    storableVaultKeyEncPubKey: p.storableVaultKeyEncPubKey,
    storableVaultKeyEncPubKeySignature: p.storableVaultKeyEncPubKeySignature,
    storableProfileSeedEncPubKey: p.storableProfileSeedEncPubKey,
    storableProfileSeedEncPubKeySignature: p.storableProfileSeedEncPubKeySignature,
    email: undefined,
    unverifiedEmail: undefined,
    authenticatorLocks: [],
    profileLock: p.profileLock ? toSyncUpdateProfileProfileLock(p.profileLock) : undefined,
  };

  return {
    adminProfile,
    entraTenantId: pbInfo.entraTenantId || undefined,
    entraPreferUserPrincipalName: pbInfo.entraPreferUserPrincipalName,
    entraSettings: pbInfo.entraSettings || undefined,
    entraSynchronizedAt:
      pbInfo.entraSynchronizedAt ? timestampDate(pbInfo.entraSynchronizedAt) : undefined,
    csvIntegrationActive: pbInfo.csvIntegrationActive,
    googleWorkspaceIntegrationActive: pbInfo.googleWorkspaceIntegrationActive,
    licenseType: pbInfo.licenseType,
    trialEndDate: pbInfo.trialEndDate ? timestampDate(pbInfo.trialEndDate) : undefined,
    parentOrganizationAdminProfileId: pbInfo.parentOrganizationAdminProfileId || undefined,
    billingOrganizationId: pbInfo.billingOrganizationId || undefined,
  };
}

function toSyncOrganization(so: SyncUpdate_Organization): SyncUpdateOrganization {
  const profiles: SyncUpdateOrganizationProfile[] = so.profiles.map((m) => ({
    profileId: m.profileId,
    isNotConnected: m.isNotConnected,
    isDelegatedAdmin: m.isDelegatedAdmin,
    isService: m.isService,
    personalVaultId: m.personalVaultId || undefined,
    // using undefined here, because this field is not persisted
    preliminaryUserId: m.preliminaryUserId || undefined,
    startCode: m.startCode || undefined,
    startCodeGeneratedAt:
      m.startCodeGeneratedAt ? timestampDate(m.startCodeGeneratedAt) : undefined,
    startCodeEmailSentAt:
      m.startCodeEmailSentAt ? timestampDate(m.startCodeEmailSentAt) : undefined,
    startCodeExpiredAt: m.startCodeExpiredAt ? timestampDate(m.startCodeExpiredAt) : undefined,
    primaryLoginDevice:
      m.primaryLoginDevice ?
        knownEnumOrDefault(m.primaryLoginDevice, PrimaryLoginDevice)
      : undefined,
    lastActiveAt: m.lastActiveAt ? timestampDate(m.lastActiveAt) : undefined,
    entraUserId: m.entraUserId || undefined,
    childOrganizationManagementShow: m.childOrganizationManagementShow,
    roleIds: m.roleIds,
  }));
  const { icon } = so;
  return {
    id: requireFieldNotEmpty(so.id),
    name: requireFieldNotEmpty(so.name),
    organizationType: knownEnumOrDefault(so.organizationType, OrganizationType),
    superUserId: requireFieldNotEmpty(so.superUserId),
    adminProfileId: requireFieldNotEmpty(so.adminProfileId),
    icon: icon.length ? icon : undefined,
    restrictPersonalLogins: so.restrictPersonalLogins,
    unlockTimeLimit: requireFieldNotEmpty(so.unlockTimeLimit),
    auditlogEnable: so.auditlogEnable,
    roleManagementEnable: so.roleManagementEnable,
    childOrganizationManagementEnable: so.childOrganizationManagementEnable,
    serviceUserEnable: so.serviceUserEnable,
    monitoringEnable: so.monitoringEnable,
    isDelegatedAdmin: so.isDelegatedAdmin,
    isPreliminary: so.isPreliminary,
    profileChildOrganizationManagementShow: so.profileChildOrganizationManagementShow,
    adminInfo: toSyncUpdateOrganizationAdminInfo(so.adminInfo),
    profiles,
    subscriptionError: so.subscriptionError || undefined,
    verifiedDomain: so.verifiedDomain || undefined,
    passwordPolicy: so.passwordPolicy || undefined,
    clientSettings: so.clientSettings || undefined,
    parentOrganizationInfo:
      so.parentOrganizationInfo ?
        {
          id: requireFieldNotEmpty(so.parentOrganizationInfo.id),
          name: requireFieldNotEmpty(so.parentOrganizationInfo.name),
          icon: so.parentOrganizationInfo.icon || undefined,
          metadata: so.parentOrganizationInfo.metadata || undefined,
        }
      : undefined,
    requiredMaintenanceTasks: so.requiredMaintenanceTasks,
    entraUserId: so.entraUserId || undefined,
    loginSummaryVaultInfo:
      so.loginSummaryVaultInfo ?
        {
          id: requireFieldNotEmpty(so.loginSummaryVaultInfo.id),
          generationId: requireFieldNotEmpty(so.loginSummaryVaultInfo.generationId),
          messagePublicKey: so.loginSummaryVaultInfo.messagePublicKey,
        }
      : undefined,
    roles: so.roles.map((r) => ({
      id: r.id,
      isPrivileged: r.isPrivileged,
      customName: r.customName || undefined,
      permissions: requireFieldNotEmpty(r.permissions),
      roleType: knownEnumOrDefault(r.roleType, RoleType),
      changedAt: timestampDate(requireFieldNotEmpty(r.changedAt)),
    })),
    roleIds: so.roleIds,
  };
}

function toSyncRelatedProfile(su: SyncUpdate_RelatedProfile): SyncUpdateRelatedProfile {
  return {
    id: requireFieldNotEmpty(su.id),
    email: su.email || undefined,
  };
}

function toSyncUpdateUser(su: SyncUpdate_User | undefined): SyncUpdateUser | undefined {
  if (su === undefined) {
    return undefined;
  }

  let authenticatorBlockHash: Uint8Array | undefined = su.authenticatorBlockHash;

  if (authenticatorBlockHash.length === 0) {
    authenticatorBlockHash = undefined;
  }

  const webauthnCredentials: SyncUpdateWebauthnCredential[] = su.webauthnCredentials.map((wc) => ({
    webauthnId: wc.webauthnId,
    credentialType: knownEnumOrDefault(wc.credentialType, CredentialType),
    aaguid: wc.aaguid || undefined,
    sessionId: wc.sessionId || undefined,
    createdAt: timestampDate(requireFieldNotEmpty(wc.createdAt)),
    lastUsedAt: timestampDate(requireFieldNotEmpty(wc.lastUsedAt)),
    prfSupportStatus: wc.prfSupportStatus,
  }));

  return {
    preliminaryEmail: su.preliminaryEmail || undefined,
    serverSideSettings: su.serverSideSettings || undefined,
    authenticatorBlockHash,
    preferredLocale: su.preferredLocale || undefined,
    webauthnCredentials,
    userClientSettings: su.userClientSettings || undefined,
    openUserConfirmationIds: su.openUserConfirmationIds,
    profilesLocked: su.profilesLocked,
  };
}

function toSyncUpdateVault(v: SyncUpdate_Vault): SyncUpdateVault {
  return {
    id: v.id,
    commitId: v.commitId,
    generationId: v.generationId,
    profileKeyGenerationId: v.profileKeyGenerationId || undefined,
    adminProfileKeyGenerationId: v.adminProfileKeyGenerationId || undefined,
    profiles: v.profiles,
    vaultType: knownEnumOrDefault(v.vaultType, VaultType),
    associatedVaultId: v.associatedVaultId || undefined,
    dirty: v.dirty,
    regenerateOnSync: v.regenerateOnSync,
    organizationId: v.organizationId || undefined,
    messagePublicKey: v.messagePublicKey.length > 0 ? v.messagePublicKey : undefined,
    hasMessagesQueued: v.hasMessagesQueued,
    inboxSlug: v.inboxSlug || undefined,
    inboxEnabled: v.inboxEnabled,
    memberSyncMode: v.memberSyncMode,
    entraGroupId: v.entraGroupId || undefined,
    requiredMaintenanceTasks: v.requiredMaintenanceTasks,
  };
}

export function toSyncDto(resp: PbSyncUpdate): SyncUpdate {
  const serverTime = timestampDate(requireFieldNotEmpty(resp.serverTime));
  const syncVersion = Number(resp.syncVersion);
  const { tokenRefreshNeeded, clientOutdated } = resp;

  const sessionUnlock = toSyncSessionUnlockDto(resp.sessionUnlock);

  const uf = resp.updatedFields;
  const user = !uf.includes(SyncUpdateField.SU_USER) ? undefined : toSyncUpdateUser(resp.user);
  const sessions =
    !uf.includes(SyncUpdateField.SU_SESSIONS) ?
      undefined
    : resp.sessions.map((s) => ({
        id: s.id,
        createdAt: timestampDate(requireFieldNotEmpty(s.createdAt)),
        lastUsedAt: timestampDate(requireFieldNotEmpty(s.lastUsedAt)),
        unlockedAt: s.unlockedAt ? timestampDate(s.unlockedAt) : undefined,
        unlockedUntil: s.unlockedUntil ? timestampDate(s.unlockedUntil) : undefined,
        unlockExtendAt: s.unlockExtendAt ? timestampDate(s.unlockExtendAt) : undefined,
        unlockRequestedAt: s.unlockRequestedAt ? timestampDate(s.unlockRequestedAt) : undefined,
        clientSettings: s.clientSettings || undefined,
        sessionType: knownEnumOrDefault(s.sessionType, SessionType),
        enabledProfileIds: s.enabledProfileIds,
        unlockTimeLimitMinutes: requireFieldNotEmpty(s.unlockTimeLimitMinutes),
      }));
  const vaults =
    !uf.includes(SyncUpdateField.SU_VAULTS) ? undefined : resp.vaults.map(toSyncUpdateVault);
  const achievementIds =
    !uf.includes(SyncUpdateField.SU_ACHIEVEMENTS) ? undefined : resp.achievements;
  const channels = !uf.includes(SyncUpdateField.SU_CHANNELS) ? undefined : resp.channels;

  const organizations =
    uf.includes(SyncUpdateField.SU_ORGANIZATIONS) ?
      resp.organizations.map(toSyncOrganization)
    : undefined;

  const relatedProfiles =
    uf.includes(SyncUpdateField.SU_RELATED_PROFILES) ?
      resp.relatedProfiles.map((p) => toSyncRelatedProfile(p))
    : undefined;

  const profiles =
    uf.includes(SyncUpdateField.SU_PROFILES) ? resp.profiles.map(toSyncUpdateProfile) : undefined;

  const preliminaryProfiles =
    uf.includes(SyncUpdateField.SU_PROFILES) ?
      resp.preliminaryProfiles.map(toSyncUpdatePreliminaryProfile)
    : undefined;

  const maintenanceSettings =
    uf.includes(SyncUpdateField.SU_MAINTENANCE_SETTINGS) ?
      { allowedTasks: requireFieldNotEmpty(resp.maintenanceSettings).allowedTasks }
    : undefined;

  return {
    serverTime,
    clientOutdated,
    sessionUnlock,
    tokenRefreshNeeded,
    user,
    sessions,
    vaults,
    achievementIds,
    channels,
    syncVersion,
    organizations,
    relatedProfiles,
    profiles,
    preliminaryProfiles,
    maintenanceSettings,
    leakedPasswordsChangeTime:
      resp.leakedPasswordsChangeTime ? timestampDate(resp.leakedPasswordsChangeTime) : undefined,
  };
}

export class SyncServiceClient {
  constructor(private readonly espbClient: Client<typeof SyncService>) {}

  async poll(fcmMessageId?: string): Promise<SyncUpdate> {
    const resp = await this.espbClient.sync({ fcmMessageId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }
}
